import { DashboardColumnFilterBadges } from "@/components/non_registrant/dashboard-column-filter-badges";
import type { ColumnFilterBadge } from "@/components/non_registrant/dashboard-column-filter-utils";
import { JurisdictionCard } from "@/components/registrant/dashboard/license-list/jurisdiction-card";
import { filterLicensesByColumnFilters } from "@/components/registrant/dashboard/license-list/license-table";
import {
  EMPTY_REGISTRANT_BULK_FACETED_VALUES,
  REGISTRANT_BULK_LICENSE_THRESHOLD,
  RegistrantBulkRegistrationsTable,
  buildRegistrantBulkAccessorKeys,
  computeRegistrantBulkFacetedValues,
} from "@/components/registrant/dashboard/registrant-bulk-registrations-table";
import { TabsContent } from "@/components/ui/tabs";
import type { UserType } from "@/constants";
import type { RegistrantLicenseListQuestionColumnSchema } from "@/hera-api/client/types.gen";
import type { JurisdictionLicenses } from "@/types/license";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import * as React from "react";

/**
 * Flatten jurisdiction groups back into one list for the bulk table.
 *
 * A server-sorted page arrives in the requested sort order, so grouping it by
 * jurisdiction has to be undone with `rowOrder` (license id -> position in the
 * API response); without it the bulk table would show each jurisdiction's rows
 * as a separate sorted run.
 */
export function flattenJurisdictionLicensesInRowOrder(
  jurisdictions: JurisdictionLicenses[],
  rowOrder?: ReadonlyMap<string, number>,
): JurisdictionLicenses["licenses"] {
  const flat = jurisdictions.flatMap((j) => j.licenses);
  if (!rowOrder || rowOrder.size === 0) return flat;
  return flat.sort(
    (left, right) =>
      (rowOrder.get(left.id) ?? 0) - (rowOrder.get(right.id) ?? 0),
  );
}

interface DashboardListProps {
  /** All Active tab: non-deleted, non-cancelled registrations */
  allActiveRegistrationsJurisdictions: JurisdictionLicenses[];
  /** Needs Attention tab: late renewals, excluding compliance issues */
  needsAttentionLicenses: JurisdictionLicenses[];
  /** Compliance Issues tab: open or ready-for-check compliance issues */
  complianceIssueLicenses?: JurisdictionLicenses[];
  /** Balance Due tab: display status Balance Due */
  balanceDueLicenses: JurisdictionLicenses[];
  /** True when the Bulk Payment dashboard tile narrowed the All Active list */
  bulkPaymentPayLaterCohortOnly?: boolean;
  renewableLicenses: JurisdictionLicenses[];
  inactiveLicenses: JurisdictionLicenses[];
  userType: UserType;
  /** When true (company with >1 user), show Registrant column in license tables */
  showRegistrantColumn?: boolean;
  /** Column visibility for jurisdiction cards (non-bulk) */
  columnVisibility?: Record<string, boolean>;
  /** Column filters for jurisdiction cards (non-bulk) */
  columnFilters?: Record<string, string[]>;
  onColumnFiltersChange?: (filters: Record<string, string[]>) => void;
  /**
   * Applied column filters for the active view. Card headers own the only filter
   * controls in card layout, so an empty result would otherwise leave no way to
   * see or clear the filter that emptied the list.
   */
  columnFilterBadges?: ColumnFilterBadge[];
  onClearColumnFilters?: () => void;
  onRemoveColumnFilter?: (badge: ColumnFilterBadge) => void;
  /** Distinct values per column for card-view filter dropdowns */
  facetedValuesByColumn?: Record<string, string[]>;
  /** Inactive tab only: open license details panel instead of navigating away */
  onInactiveLicenseRowClick?: (licenseId: string) => void;
  questionColumns?: RegistrantLicenseListQuestionColumnSchema[];
  bulkColumnFilters: Record<string, string[]>;
  onBulkColumnFiltersChange: (filters: Record<string, string[]>) => void;
  bulkColumnVisibility: Record<string, boolean>;
  onBulkColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  onBulkLicenseRowClick: (licenseId: string) => void;
  listTotalCount?: number;
  /** Unfiltered count for bulk vs card layout (ignores column filters). */
  bulkLayoutTotalCount?: number;
  serverMode?: boolean;
  /** License id -> position in the server-sorted page (server mode only). */
  serverRowOrder?: ReadonlyMap<string, number>;
  pageIndex?: number;
  pageSize?: number;
  onPaginationChange?: (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  dataUpdatedAt?: number;
  onFilterPopoverOpen?: (columnId: string, search?: string) => void;
  onServerExport?: (
    mode: "visible" | "all",
    format: "csv" | "excel",
  ) => void | Promise<void>;
  leadingContent?: React.ReactNode;
}

export function DashboardList({
  allActiveRegistrationsJurisdictions,
  needsAttentionLicenses,
  complianceIssueLicenses = [],
  balanceDueLicenses,
  bulkPaymentPayLaterCohortOnly = false,
  renewableLicenses,
  inactiveLicenses,
  userType,
  showRegistrantColumn = false,
  columnVisibility,
  columnFilters,
  onColumnFiltersChange,
  columnFilterBadges = [],
  onClearColumnFilters,
  onRemoveColumnFilter,
  facetedValuesByColumn,
  onInactiveLicenseRowClick,
  questionColumns = [],
  bulkColumnFilters,
  onBulkColumnFiltersChange,
  bulkColumnVisibility,
  onBulkColumnVisibilityChange,
  onBulkLicenseRowClick,
  listTotalCount,
  bulkLayoutTotalCount,
  serverMode = false,
  serverRowOrder,
  pageIndex,
  pageSize,
  onPaginationChange,
  sorting,
  onSortingChange,
  onRefresh,
  isRefreshing,
  dataUpdatedAt,
  onFilterPopoverOpen,
  onServerExport,
  leadingContent,
}: DashboardListProps) {
  const totalAll = React.useMemo(
    () =>
      allActiveRegistrationsJurisdictions.reduce(
        (n, j) => n + j.licenses.length,
        0,
      ),
    [allActiveRegistrationsJurisdictions],
  );
  const totalNeedsAttention = React.useMemo(
    () => needsAttentionLicenses.reduce((n, j) => n + j.licenses.length, 0),
    [needsAttentionLicenses],
  );
  const totalComplianceIssues = React.useMemo(
    () => complianceIssueLicenses.reduce((n, j) => n + j.licenses.length, 0),
    [complianceIssueLicenses],
  );
  const totalBalanceDue = React.useMemo(
    () => balanceDueLicenses.reduce((n, j) => n + j.licenses.length, 0),
    [balanceDueLicenses],
  );
  const totalRenewable = React.useMemo(
    () => renewableLicenses.reduce((n, j) => n + j.licenses.length, 0),
    [renewableLicenses],
  );
  const totalInactive = React.useMemo(
    () => inactiveLicenses.reduce((n, j) => n + j.licenses.length, 0),
    [inactiveLicenses],
  );

  const bulkLayoutCount = bulkLayoutTotalCount ?? listTotalCount;

  const useBulkAll =
    (bulkLayoutCount ?? totalAll) > REGISTRANT_BULK_LICENSE_THRESHOLD;
  const useBulkNeedsAttention =
    (bulkLayoutCount ?? totalNeedsAttention) >
    REGISTRANT_BULK_LICENSE_THRESHOLD;
  const useBulkComplianceIssues =
    (bulkLayoutCount ?? totalComplianceIssues) >
    REGISTRANT_BULK_LICENSE_THRESHOLD;
  const useBulkBalanceDue =
    (bulkLayoutCount ?? totalBalanceDue) > REGISTRANT_BULK_LICENSE_THRESHOLD;
  const useBulkRenewable =
    (bulkLayoutCount ?? totalRenewable) > REGISTRANT_BULK_LICENSE_THRESHOLD;
  const useBulkInactive =
    (bulkLayoutCount ?? totalInactive) > REGISTRANT_BULK_LICENSE_THRESHOLD;

  const bulkAccessorKeys = React.useMemo(
    () =>
      buildRegistrantBulkAccessorKeys(showRegistrantColumn, questionColumns),
    [showRegistrantColumn, questionColumns],
  );

  const flatUnfilteredAll = React.useMemo(
    () =>
      flattenJurisdictionLicensesInRowOrder(
        allActiveRegistrationsJurisdictions,
        serverRowOrder,
      ),
    [allActiveRegistrationsJurisdictions, serverRowOrder],
  );
  const flatUnfilteredNeedsAttention = React.useMemo(
    () =>
      flattenJurisdictionLicensesInRowOrder(
        needsAttentionLicenses,
        serverRowOrder,
      ),
    [needsAttentionLicenses, serverRowOrder],
  );
  const flatUnfilteredComplianceIssues = React.useMemo(
    () =>
      flattenJurisdictionLicensesInRowOrder(
        complianceIssueLicenses,
        serverRowOrder,
      ),
    [complianceIssueLicenses, serverRowOrder],
  );
  const flatUnfilteredBalanceDue = React.useMemo(
    () =>
      flattenJurisdictionLicensesInRowOrder(balanceDueLicenses, serverRowOrder),
    [balanceDueLicenses, serverRowOrder],
  );
  const flatUnfilteredRenewable = React.useMemo(
    () =>
      flattenJurisdictionLicensesInRowOrder(renewableLicenses, serverRowOrder),
    [renewableLicenses, serverRowOrder],
  );
  const flatUnfilteredInactive = React.useMemo(
    () =>
      flattenJurisdictionLicensesInRowOrder(inactiveLicenses, serverRowOrder),
    [inactiveLicenses, serverRowOrder],
  );

  const licensesForBulkFacetsAll = React.useMemo(() => {
    let pool = flatUnfilteredAll;
    if (bulkPaymentPayLaterCohortOnly) {
      pool = pool.filter((l) => l.bulk_payment_pay_later_eligible);
    }
    return pool;
  }, [flatUnfilteredAll, bulkPaymentPayLaterCohortOnly]);

  const bulkFacetsAll = React.useMemo(
    () =>
      computeRegistrantBulkFacetedValues(
        licensesForBulkFacetsAll,
        bulkAccessorKeys,
      ),
    [licensesForBulkFacetsAll, bulkAccessorKeys],
  );
  const bulkFacetsNeedsAttention = React.useMemo(
    () =>
      computeRegistrantBulkFacetedValues(
        flatUnfilteredNeedsAttention,
        bulkAccessorKeys,
      ),
    [flatUnfilteredNeedsAttention, bulkAccessorKeys],
  );
  const bulkFacetsComplianceIssues = React.useMemo(
    () =>
      computeRegistrantBulkFacetedValues(
        flatUnfilteredComplianceIssues,
        bulkAccessorKeys,
      ),
    [flatUnfilteredComplianceIssues, bulkAccessorKeys],
  );
  const bulkFacetsBalanceDue = React.useMemo(
    () =>
      computeRegistrantBulkFacetedValues(
        flatUnfilteredBalanceDue,
        bulkAccessorKeys,
      ),
    [flatUnfilteredBalanceDue, bulkAccessorKeys],
  );
  const bulkFacetsRenewable = React.useMemo(
    () =>
      computeRegistrantBulkFacetedValues(
        flatUnfilteredRenewable,
        bulkAccessorKeys,
      ),
    [flatUnfilteredRenewable, bulkAccessorKeys],
  );
  const bulkFacetsInactive = React.useMemo(
    () =>
      computeRegistrantBulkFacetedValues(
        flatUnfilteredInactive,
        bulkAccessorKeys,
      ),
    [flatUnfilteredInactive, bulkAccessorKeys],
  );

  const filteredFlatAll = React.useMemo(
    () =>
      allActiveRegistrationsJurisdictions.flatMap((j) => {
        let pool = j.licenses;
        if (bulkPaymentPayLaterCohortOnly) {
          pool = pool.filter((l) => l.bulk_payment_pay_later_eligible);
        }
        return filterLicensesByColumnFilters(pool, bulkColumnFilters);
      }),
    [
      allActiveRegistrationsJurisdictions,
      bulkColumnFilters,
      bulkPaymentPayLaterCohortOnly,
    ],
  );
  const filteredFlatNeedsAttention = React.useMemo(
    () =>
      needsAttentionLicenses.flatMap((j) =>
        filterLicensesByColumnFilters(j.licenses, bulkColumnFilters),
      ),
    [needsAttentionLicenses, bulkColumnFilters],
  );
  const filteredFlatComplianceIssues = React.useMemo(
    () =>
      complianceIssueLicenses.flatMap((j) =>
        filterLicensesByColumnFilters(j.licenses, bulkColumnFilters),
      ),
    [complianceIssueLicenses, bulkColumnFilters],
  );
  const filteredFlatBalanceDue = React.useMemo(
    () =>
      balanceDueLicenses.flatMap((j) =>
        filterLicensesByColumnFilters(j.licenses, bulkColumnFilters),
      ),
    [balanceDueLicenses, bulkColumnFilters],
  );
  const filteredFlatRenewable = React.useMemo(
    () =>
      renewableLicenses.flatMap((j) =>
        filterLicensesByColumnFilters(j.licenses, bulkColumnFilters),
      ),
    [renewableLicenses, bulkColumnFilters],
  );
  const filteredFlatInactive = React.useMemo(
    () =>
      inactiveLicenses.flatMap((j) =>
        filterLicensesByColumnFilters(j.licenses, bulkColumnFilters),
      ),
    [inactiveLicenses, bulkColumnFilters],
  );

  const bulkServerProps = serverMode
    ? {
        serverMode: true as const,
        lockToViewport: true,
        rowCount: listTotalCount,
        pageIndex,
        pageSize,
        onPaginationChange,
        sorting,
        onSortingChange,
        onRefresh,
        isRefreshing,
        dataUpdatedAt,
        onFilterPopoverOpen,
        onServerExport,
      }
    : {};

  const emptyWorkspace = (message: string) => (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-clip overflow-y-auto">
      {leadingContent}
      <DashboardColumnFilterBadges
        badges={columnFilterBadges}
        onClearAll={onClearColumnFilters}
        onRemove={onRemoveColumnFilter}
      />
      <div className="text-center py-8">
        <p className="text-muted-foreground">{message}</p>
      </div>
    </div>
  );

  const cardWorkspace = ({
    jurisdictions,
    emptyMessage,
    cohortOnly = false,
    onLicenseRowClick,
  }: {
    jurisdictions: JurisdictionLicenses[];
    emptyMessage: string;
    cohortOnly?: boolean;
    onLicenseRowClick?: (licenseId: string) => void;
  }) => {
    const cards = jurisdictions
      .map((jurisdiction) => ({
        jurisdiction,
        filteredLicenses: serverMode
          ? jurisdiction.licenses
          : filterLicensesByColumnFilters(
              cohortOnly
                ? jurisdiction.licenses.filter(
                    (l) => l.bulk_payment_pay_later_eligible,
                  )
                : jurisdiction.licenses,
              columnFilters,
            ),
      }))
      .filter(({ filteredLicenses }) => filteredLicenses.length > 0);

    if (cards.length === 0) {
      return emptyWorkspace(emptyMessage);
    }

    return (
      <div
        data-slot="registrant-card-list-scroll"
        className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-clip overflow-y-auto"
      >
        {leadingContent}
        {cards.map(({ jurisdiction, filteredLicenses }) => (
          <JurisdictionCard
            key={jurisdiction.display_code}
            jurisdiction={jurisdiction.name}
            licenses={filteredLicenses}
            userType={userType}
            showRegistrantColumn={showRegistrantColumn}
            columnVisibility={columnVisibility}
            columnFilters={columnFilters}
            onColumnFiltersChange={onColumnFiltersChange}
            facetedValuesByColumn={facetedValuesByColumn}
            onLicenseRowClick={onLicenseRowClick}
            questionColumns={questionColumns}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <TabsContent
        value="all"
        className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=inactive]:hidden"
      >
        {useBulkAll ? (
          <RegistrantBulkRegistrationsTable
            licenses={serverMode ? flatUnfilteredAll : filteredFlatAll}
            questionColumns={questionColumns}
            showRegistrantColumn={showRegistrantColumn}
            columnFilters={bulkColumnFilters}
            onColumnFiltersChange={onBulkColumnFiltersChange}
            columnVisibility={bulkColumnVisibility}
            onColumnVisibilityChange={onBulkColumnVisibilityChange}
            facetedValuesByColumn={
              serverMode
                ? (facetedValuesByColumn ??
                  EMPTY_REGISTRANT_BULK_FACETED_VALUES)
                : bulkFacetsAll
            }
            onLicenseRowClick={onBulkLicenseRowClick}
            leadingContent={leadingContent}
            {...bulkServerProps}
          />
        ) : (
          cardWorkspace({
            jurisdictions: allActiveRegistrationsJurisdictions,
            cohortOnly: bulkPaymentPayLaterCohortOnly,
            emptyMessage: bulkPaymentPayLaterCohortOnly
              ? "No registrations match the current filters."
              : "No active registrations match the current filters.",
            onLicenseRowClick: onBulkLicenseRowClick,
          })
        )}
      </TabsContent>

      <TabsContent
        value="compliance-issues"
        className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=inactive]:hidden"
      >
        {useBulkComplianceIssues ? (
          <RegistrantBulkRegistrationsTable
            licenses={
              serverMode
                ? flatUnfilteredComplianceIssues
                : filteredFlatComplianceIssues
            }
            questionColumns={questionColumns}
            showRegistrantColumn={showRegistrantColumn}
            columnFilters={bulkColumnFilters}
            onColumnFiltersChange={onBulkColumnFiltersChange}
            columnVisibility={bulkColumnVisibility}
            onColumnVisibilityChange={onBulkColumnVisibilityChange}
            facetedValuesByColumn={
              serverMode
                ? (facetedValuesByColumn ??
                  EMPTY_REGISTRANT_BULK_FACETED_VALUES)
                : bulkFacetsComplianceIssues
            }
            onLicenseRowClick={onBulkLicenseRowClick}
            leadingContent={leadingContent}
            {...bulkServerProps}
          />
        ) : (
          cardWorkspace({
            jurisdictions: complianceIssueLicenses,
            emptyMessage:
              "No registrations with a compliance issue found in the selected jurisdictions.",
            onLicenseRowClick: onBulkLicenseRowClick,
          })
        )}
      </TabsContent>

      <TabsContent
        value="needs-attention"
        className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=inactive]:hidden"
      >
        {useBulkNeedsAttention ? (
          <RegistrantBulkRegistrationsTable
            licenses={
              serverMode
                ? flatUnfilteredNeedsAttention
                : filteredFlatNeedsAttention
            }
            questionColumns={questionColumns}
            showRegistrantColumn={showRegistrantColumn}
            columnFilters={bulkColumnFilters}
            onColumnFiltersChange={onBulkColumnFiltersChange}
            columnVisibility={bulkColumnVisibility}
            onColumnVisibilityChange={onBulkColumnVisibilityChange}
            facetedValuesByColumn={
              serverMode
                ? (facetedValuesByColumn ??
                  EMPTY_REGISTRANT_BULK_FACETED_VALUES)
                : bulkFacetsNeedsAttention
            }
            onLicenseRowClick={onBulkLicenseRowClick}
            leadingContent={leadingContent}
            {...bulkServerProps}
          />
        ) : (
          cardWorkspace({
            jurisdictions: needsAttentionLicenses,
            emptyMessage:
              "No registrations needing attention found in the selected jurisdictions.",
            onLicenseRowClick: onBulkLicenseRowClick,
          })
        )}
      </TabsContent>

      <TabsContent
        value="balance-due"
        className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=inactive]:hidden"
      >
        {useBulkBalanceDue ? (
          <RegistrantBulkRegistrationsTable
            licenses={
              serverMode ? flatUnfilteredBalanceDue : filteredFlatBalanceDue
            }
            questionColumns={questionColumns}
            showRegistrantColumn={showRegistrantColumn}
            columnFilters={bulkColumnFilters}
            onColumnFiltersChange={onBulkColumnFiltersChange}
            columnVisibility={bulkColumnVisibility}
            onColumnVisibilityChange={onBulkColumnVisibilityChange}
            facetedValuesByColumn={
              serverMode
                ? (facetedValuesByColumn ??
                  EMPTY_REGISTRANT_BULK_FACETED_VALUES)
                : bulkFacetsBalanceDue
            }
            onLicenseRowClick={onBulkLicenseRowClick}
            leadingContent={leadingContent}
            {...bulkServerProps}
          />
        ) : (
          cardWorkspace({
            jurisdictions: balanceDueLicenses,
            emptyMessage:
              "No registrations with a balance due found in the selected jurisdictions.",
            onLicenseRowClick: onBulkLicenseRowClick,
          })
        )}
      </TabsContent>

      <TabsContent
        value="renewable"
        className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=inactive]:hidden"
      >
        {useBulkRenewable ? (
          <RegistrantBulkRegistrationsTable
            licenses={
              serverMode ? flatUnfilteredRenewable : filteredFlatRenewable
            }
            questionColumns={questionColumns}
            showRegistrantColumn={showRegistrantColumn}
            columnFilters={bulkColumnFilters}
            onColumnFiltersChange={onBulkColumnFiltersChange}
            columnVisibility={bulkColumnVisibility}
            onColumnVisibilityChange={onBulkColumnVisibilityChange}
            facetedValuesByColumn={
              serverMode
                ? (facetedValuesByColumn ??
                  EMPTY_REGISTRANT_BULK_FACETED_VALUES)
                : bulkFacetsRenewable
            }
            onLicenseRowClick={onBulkLicenseRowClick}
            leadingContent={leadingContent}
            {...bulkServerProps}
          />
        ) : (
          cardWorkspace({
            jurisdictions: renewableLicenses,
            emptyMessage:
              "No renewable registrations found in the selected jurisdictions.",
            onLicenseRowClick: onBulkLicenseRowClick,
          })
        )}
      </TabsContent>

      <TabsContent
        value="inactive"
        className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=inactive]:hidden"
      >
        {useBulkInactive ? (
          <RegistrantBulkRegistrationsTable
            licenses={
              serverMode ? flatUnfilteredInactive : filteredFlatInactive
            }
            questionColumns={questionColumns}
            showRegistrantColumn={showRegistrantColumn}
            inactiveTab
            columnFilters={bulkColumnFilters}
            onColumnFiltersChange={onBulkColumnFiltersChange}
            columnVisibility={bulkColumnVisibility}
            onColumnVisibilityChange={onBulkColumnVisibilityChange}
            facetedValuesByColumn={
              serverMode
                ? (facetedValuesByColumn ??
                  EMPTY_REGISTRANT_BULK_FACETED_VALUES)
                : bulkFacetsInactive
            }
            onLicenseRowClick={onBulkLicenseRowClick}
            leadingContent={leadingContent}
            {...bulkServerProps}
          />
        ) : (
          cardWorkspace({
            jurisdictions: inactiveLicenses,
            emptyMessage:
              "No inactive registrations found in the selected jurisdictions.",
            onLicenseRowClick:
              onInactiveLicenseRowClick ?? onBulkLicenseRowClick,
          })
        )}
      </TabsContent>
    </>
  );
}
